import React, { useEffect, useState } from "react";
import { supabase } from "./supabase";

interface LiveRostersProps {
  isAdmin?: boolean;
  sessionId?: string;
}

export const LiveRosters = ({
  isAdmin = false,
  sessionId,
}: LiveRostersProps) => {
  const [teams, setTeams] = useState<any[]>([]);
  const [picks, setPicks] = useState<any[]>([]);

  const fetchRosterData = async () => {
    if (!sessionId) return;

    const { data: tData } = await supabase
      .from("teams")
      .select("*")
      .order("draft_position", { ascending: true });

    const { data: pData } = await supabase
      .from("picks")
      .select(
        `
        id,
        team_id,
        player:players(name, position)
      `
      )
      .eq("session_id", sessionId);

    if (tData) setTeams(tData);
    if (pData) setPicks(pData);
  };

  useEffect(() => {
    fetchRosterData();

    const channel = supabase
      .channel(`roster-sync-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "picks",
          filter: `session_id=eq.${sessionId}`,
        },
        () => fetchRosterData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  const downloadCSV = (filename: string, csvContent: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadTeamCSV = (teamName: string, teamPicks: any[]) => {
    const headers = "Pick,Player Name,Position\n";
    const content = teamPicks
      .map((p, idx) => `${idx + 1},"${p.player?.name}",${p.player?.position}`)
      .join("\n");
    downloadCSV(
      `${teamName.replace(/\s+/g, "_")}_Roster.csv`,
      headers + content
    );
  };

  const downloadAllRosters = () => {
    const headers = "Team,Pick,Player Name,Position\n";
    const content = teams
      .map((team) => {
        const teamPicks = picks.filter((p) => p.team_id === team.id);
        return teamPicks
          .map(
            (p, idx) =>
              `"${team.team_name}",${idx + 1},"${p.player?.name}",${
                p.player?.position
              }`
          )
          .join("\n");
      })
      .filter((row) => row !== "")
      .join("\n");

    downloadCSV("Empire_Master_Rosters.csv", headers + content);
  };

  if (!sessionId) return null;

  return (
    <div style={{ marginTop: "50px", width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2px solid #22565a",
          marginBottom: "30px",
          paddingBottom: "10px",
        }}
      >
        <h2
          style={{
            color: "#22565a",
            fontFamily: "'Oswald', sans-serif",
            margin: 0,
            textTransform: "uppercase",
          }}
        >
          OFFICIAL TEAM ROSTERS
        </h2>

        {isAdmin && picks.length > 0 && (
          <button
            onClick={downloadAllRosters}
            style={{
              background: "#22565a",
              color: "#f9eeda",
              border: "none",
              padding: "8px 16px",
              fontFamily: "'Oswald', sans-serif",
              cursor: "pointer",
              borderRadius: "4px",
              fontSize: "0.8rem",
              fontWeight: "bold",
              textTransform: "uppercase",
            }}
          >
            Download All Rosters
          </button>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
        }}
      >
        {teams.map((team) => {
          const teamPicks = picks.filter((p) => p.team_id === team.id);
          return (
            <div
              key={team.id}
              style={{
                background: "#000",
                borderTop: `4px solid ${team.primary_color || "#22565a"}`,
                borderRadius: "4px",
                overflow: "hidden",
                borderLeft: "1px solid #1a1a1a",
                borderRight: "1px solid #1a1a1a",
                borderBottom: "1px solid #1a1a1a",
              }}
            >
              <div
                style={{
                  background: "#111",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {team.logo_url && (
                    <img
                      src={team.logo_url}
                      alt=""
                      style={{
                        height: "22px",
                        width: "22px",
                        objectFit: "contain",
                      }}
                    />
                  )}
                  <h3
                    style={{
                      margin: 0,
                      color: "#f9eeda",
                      fontFamily: "'Oswald', sans-serif",
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                    }}
                  >
                    {team.team_name}
                  </h3>
                </div>

                <div
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <div
                    style={{
                      background: "#22565a",
                      color: "#f9eeda",
                      fontFamily: "'Oswald', sans-serif",
                      fontSize: "0.65rem",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontWeight: "bold",
                    }}
                  >
                    {teamPicks.length}
                  </div>

                  {isAdmin && teamPicks.length > 0 && (
                    <button
                      onClick={() => downloadTeamCSV(team.team_name, teamPicks)}
                      style={{
                        background: "transparent",
                        border: "1px solid #444",
                        color: "#888",
                        cursor: "pointer",
                        fontSize: "0.6rem",
                        padding: "2px 5px",
                        borderRadius: "4px",
                        fontFamily: "'Oswald', sans-serif",
                        textTransform: "uppercase",
                      }}
                    >
                      CSV
                    </button>
                  )}
                </div>
              </div>

              <div style={{ padding: "15px" }}>
                {teamPicks.length === 0 ? (
                  <p
                    style={{
                      color: "#333",
                      textAlign: "center",
                      fontSize: "0.75rem",
                      fontFamily: "'Oswald', sans-serif",
                    }}
                  >
                    NO PLAYERS DRAFTED YET
                  </p>
                ) : (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {teamPicks.map((p, idx) => (
                      <li
                        key={p.id}
                        style={{
                          padding: "6px 0",
                          borderBottom: "1px solid #151515",
                          color: "#f9eeda",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "0.8rem",
                          fontFamily: "'Oswald', sans-serif",
                        }}
                      >
                        <span style={{ textTransform: "uppercase" }}>
                          <span
                            style={{ color: "#22565a", marginRight: "8px" }}
                          >
                            {idx + 1}.
                          </span>
                          {p.player?.name}
                        </span>
                        <span
                          style={{
                            color: "#444",
                            fontSize: "0.7rem",
                            fontWeight: "bold",
                          }}
                        >
                          {p.player?.position}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
